import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import { QUERY_CASE_OPEN_BANKING } from "api/queries/caseTracker";
import {
  QUERY_CASE_APPLICATION_SUMMARY,
  QUERY_CASE_DETAIL,
} from "api/queries/cases";
import GenericError from "components/GenericError";
import PageBreadcrumb from "components/Page/Breadcrumb";
import usePageTitle from "hooks/usePageTitle";
import { APP_URLS } from "settings";
import { useUserStore } from "store/User";
import { CaseOpenBankingData } from "ts/interfaces/caseTracker";
import { Case } from "ts/interfaces/common/case";
import { Illustration } from "ts/interfaces/common/illustration";
import { CASE_COMPLETE_KEY } from "utils/consts";
import Applicants from "./components/Applicants";
import Applications from "./components/Applications";
import CaseActions from "./components/CaseActions";
import CaseHeader from "./components/CaseHeader";
import CaseTimeline from "./components/CaseTimeline";
import LoanSummary from "./components/LoanSummary";
import Notifications from "./components/Notifications";
import ReopenApplicationDialog from "./components/ReopenApplicationDialog";
import {
  CaseHeaderSkeleton,
  ListCardSkeleton,
  SummaryCardSkeleton,
} from "./components/Skeletons";
import Tasks from "./components/Tasks";
import useReopenApplication from "./hooks/useReopenApplication";
import { ApplicationSummaryData, CaseApplication } from "./types";

interface CaseDetailData {
  cases: {
    case:
      | (Case & {
          state: string;
          canSubmitApplication: boolean;
          illustrations: Illustration[];
          applications: CaseApplication[];
        })
      | null;
  };
}

const CaseDetail: React.FC = () => {
  const { caseId = "" } = useParams<{
    caseId: string;
  }>();

  const { loading, error, data, refetch } = useQuery<CaseDetailData>(
    QUERY_CASE_DETAIL,
    {
      variables: {
        uuid: caseId,
      },
      fetchPolicy: "no-cache",
    },
  );

  const detail = data?.cases?.case;
  usePageTitle(detail?.reference && `Case ${detail.reference}`);

  const latestSubmission = detail?.applications.find(
    (app) => app.isLatestSubmission,
  );

  const { loading: summaryLoading, data: summaryData } =
    useQuery<ApplicationSummaryData>(QUERY_CASE_APPLICATION_SUMMARY, {
      variables: { uuid: latestSubmission?.uuid },
      skip: !latestSubmission,
      fetchPolicy: "no-cache",
    });
  const applicationSummary = summaryData?.data?.application;

  const { data: openBankingData } = useQuery<CaseOpenBankingData>(
    QUERY_CASE_OPEN_BANKING,
    {
      variables: { search: detail?.reference },
      skip: !latestSubmission,
      fetchPolicy: "no-cache",
    },
  );
  const openBankingRow = openBankingData?.caseTracker.cases.results.find(
    (row) => row.uuid === detail?.uuid,
  );
  // The tracker numbers applicants in creation order, as the summary lists them.
  const openBankingLinked = openBankingRow && [
    openBankingRow.applicantOneOpenBankingLinked,
    openBankingRow.applicantTwoOpenBankingLinked,
  ];

  const caseComplete = detail?.status === CASE_COMPLETE_KEY;
  // Only brokers have the application form routes; firm admins would get a 404.
  const isBroker = useUserStore((state) => !!state.user?.isBroker);
  const reopen = useReopenApplication(
    detail?.applications ?? [],
    detail?.canSubmitApplication ?? false,
    isBroker,
    async () => (await refetch()).data.cases.case?.applications ?? [],
  );

  const renderContent = () => {
    if (!loading && !detail) {
      return <GenericError error={error} message="Case not found" />;
    }

    return (
      <div className="space-y-6">
        {detail ? (
          <>
            <CaseHeader
              caseDetail={detail}
              latestSubmission={latestSubmission}
            />
            <CaseTimeline state={detail.state} />
          </>
        ) : (
          <CaseHeaderSkeleton />
        )}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Notifications />
            <Tasks
              caseId={caseId}
              canReopen={reopen.canReopen}
              onReopen={reopen.openDialog}
            />
            {detail ? (
              <Applications
                caseDetail={detail}
                caseComplete={caseComplete}
                canContinue={isBroker}
                applications={detail.applications}
                illustrations={detail.illustrations}
              />
            ) : (
              <ListCardSkeleton rows={2} />
            )}
          </div>
          <div className="space-y-6">
            <CaseActions
              canReopen={reopen.canReopen}
              onReopen={reopen.openDialog}
            />
            {applicationSummary ? (
              <>
                <LoanSummary application={applicationSummary} />
                <Applicants
                  application={applicationSummary}
                  openBankingLinked={openBankingLinked}
                />
              </>
            ) : (
              (loading || summaryLoading) && (
                <>
                  <SummaryCardSkeleton lines={3} />
                  <SummaryCardSkeleton lines={2} />
                </>
              )
            )}
          </div>
        </div>
        <ReopenApplicationDialog {...reopen.dialogProps} />
      </div>
    );
  };

  return (
    <>
      <PageBreadcrumb
        items={[
          { label: "Case tracker", to: APP_URLS.CASE_TRACKER },
          { label: detail?.reference ?? (loading ? "Loading…" : "") },
        ]}
      />

      <div className="min-h-[calc(100vh-400px)]">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default CaseDetail;
